import React, { useEffect, useRef } from "react";
import './VoucherView2.css'; // Import the CSS file for custom styles
import { Container, Table, Row, Col, Card } from "react-bootstrap";
import { useParams, useSearchParams } from "react-router";
import Logo from "../../../../shared/components/Logo";
import OtaOrLogo from "../../../pms-calendar/components/calendar/OtaOrLogo";
import { upperCase } from "lodash";
import { PaymentTypes } from "../../types/PaymentTypes";
import useIndexBookingVoucher from "../../../../services/booking-voucher/hooks/useIndexBookingVoucher";
import FullPageSpinner from "../../../../shared/components/spinner/FullPageSpinner";
import FullPageError from "../../../../shared/components/errors/FullPageError";
import { useSkinData } from "../../../../services/common/hooks/useSkinData";
import useFavicon from "../../../../shared/hooks/useFavicon";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const baseUrl = window.location.origin;
const base64Url = btoa(baseUrl); // Encode the base URL in base64 format

// =================================================================
// OPTION 2: CLASSIC TWO-COLUMN DESIGN (ENGLISH)
// =================================================================
const VoucherViewComponentNew2: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [searchParams] = useSearchParams();
    const isDownloadAction = searchParams.has("download");
    const pdfRef = useRef<HTMLDivElement>(null);

    const {
        data: skinData,
        isLoading: isSkinLoading,
        isError: isSkinError
    } = useSkinData(base64Url);

    const { data: booking, isFetching, isSuccess } = useIndexBookingVoucher({
        voucher_number: id,
    });

    useFavicon(skinData?.data?.favicon_url || null);

    // Ambil warna dari skinData, jika tidak ada, gunakan warna default
    const themeColor = skinData?.data?.color || '#076a4e';

    const generatePDF = async () => {
        const input = pdfRef.current;
        if (!input) return;

        try {
            const canvas = await html2canvas(input, {
                scale: 2,
                useCORS: true,
                width: input.scrollWidth,
                height: input.scrollHeight,
                windowWidth: input.scrollWidth,
                windowHeight: input.scrollHeight,
            });

            const imgData = canvas.toDataURL("image/png");
            const ratio = canvas.height / canvas.width;
            const pdfWidth = 210;
            const pdfHeight = pdfWidth * ratio;

            const pdf = new jsPDF({
                orientation: "landscape",
                unit: "mm",
                format: [pdfWidth, pdfHeight],
            });

            pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
            pdf.save(`Voucher-${booking?.data?.channex_unique_id || id}.pdf`);
        } catch (error) {
            console.error("Gagal membuat PDF:", error);
            alert("An error occurred while generating the PDF.");
        }
    };

    useEffect(() => {
        // Pilih elemen root dari dokumen (<html>)
        const root = document.documentElement;

        // Setel nilai variabel CSS --tblr-primary secara dinamis
        root.style.setProperty('--tblr-primary', themeColor);

        document.title = skinData?.data?.name || 'Admin Panel'; // Set judul halaman

        // Cleanup function (opsional, tapi praktik yang baik)
        // Ini akan mengembalikan warna ke default jika komponen di-unmount
        return () => {
            root.style.removeProperty('--tblr-primary');

            document.title = 'Admin Panel'; // Reset title to default on unmount
        };
    }, [themeColor]);

    useEffect(() => {
        if (isSuccess && !isFetching && booking?.data && isDownloadAction) {
            generatePDF();
        }
    }, [isSuccess, isFetching, booking?.data, isDownloadAction]);

    if (isSkinLoading || isFetching) {
        return <FullPageSpinner />;
    }

    if (isSkinError) {
        return <FullPageError message="Failed to load skin data. Please try again later." />;
    }

    if (!id || (booking && !booking.data)) {
        return (
            <Container fluid className="d-flex justify-content-center py-5 bg-secondary">
                <Card className="p-4 shadow-sm">
                    <h4 className="text-primary text-center fw-bold">Invoice not found</h4>
                </Card>
            </Container>
        );
    }

    const reservationData = booking?.data;

    function numberFormat(number: number | string): string {
        return new Intl.NumberFormat('id-ID').format(Number(number));
    }

    function replaceUnderscoreWithSpace(str: string): string {
        if (!str) return 'N/A';
        return str.replace(/_/g, ' ');
    }
    // --- End of unchanged section ---

    return (
        // Langkah 2: Bungkus semua konten dengan div.invoice-style-voucher
        <div className="invoice-style-voucher" ref={pdfRef}>
            {!isFetching && isSuccess && reservationData && (
                <Container className="my-5" style={{ maxWidth: "850px", margin: "0 auto" }}>
                    {/* Mengganti div default dengan .voucher-sheet dan menghapus style inline */}
                    <div className="voucher-sheet">
                        {/* Header */}
                        <Row className="voucher-header pb-3 mb-4 align-items-center">
                            <Col md={6}>
                                <Logo src={skinData?.data?.logo_url} style={{ height: "50px" }} />
                            </Col>
                            <Col md={6} className="text-md-end mt-3 mt-md-0">
                                <h2 className="mb-1">RESERVATION VOUCHER</h2>
                                <span>#{reservationData.channex_unique_id}</span>
                            </Col>
                        </Row>

                        {/* Main Info in Two Columns */}
                        <Row className="mb-4 info-section">
                            <Col md={6}>
                                <h5>Guest Information:</h5>
                                <p className="mb-1 fw-bold">{reservationData.channex_customer.name} {reservationData.channex_customer.surname}</p>
                                {/* PERBAIKAN: Jadikan email sebagai link */}
                                <p className="mb-1">{reservationData.channex_customer.phone} {reservationData.channex_customer.phone ? '/' : ''} <a href={`mailto:${reservationData.channex_customer.mail}`}>{reservationData.channex_customer.mail}</a></p>
                                <p className="text-muted">{`${reservationData.channex_customer.address ?? ''} ${reservationData.channex_customer.city ?? ''}`}</p>
                            </Col>
                            <Col md={6} className="text-md-end mt-4 mt-md-0">
                                <h5>Reservation Details:</h5>
                                <p className="mb-1"><strong>Booking Date:</strong> {reservationData.channex_inserted_at}</p>
                                <p className="mb-1"><strong>Total Amount:</strong> {`${numberFormat(reservationData.channex_amount)} ${reservationData.channex_currency}`}</p>
                                {/* PERBAIKAN: Tambahkan total komisi */}
                                {reservationData.channex_ota_commission > 0 && (
                                    <p className="mb-1"><strong>Total Commission:</strong> {`${numberFormat(reservationData.channex_ota_commission)} ${reservationData.channex_currency}`}</p>
                                )}
                                <p className="mb-1"><strong>Payment Type:</strong> {PaymentTypes.find(pt => pt.value === reservationData.channex_payment_collect)?.label ?? 'N/A'}</p>
                                <p className="mb-1"><strong>Payment Info:</strong> {replaceUnderscoreWithSpace(reservationData.channex_payment_type)}</p>
                                <div className="d-flex justify-content-md-end align-items-center mt-2">
                                    <strong className="me-2">Source:</strong>
                                    <OtaOrLogo otaName={reservationData.channex_ota_name ?? ''} source={reservationData.source} logoUrl={reservationData.property?.logo_url ?? undefined} propertyName={reservationData.property?.name ?? ''} />
                                </div>
                            </Col>
                        </Row>

                        {/* PERBAIKAN: Tambahkan catatan tamu jika ada */}
                        {reservationData.channex_notes && (
                            <div className="notes-section mb-4">
                                <h5>Guest Remarks:</h5>
                                <p className="text-muted fst-italic">"{reservationData.channex_notes}"</p>
                            </div>
                        )}

                        <hr />

                        {/* Room Details */}
                        <h4 className="text-center my-4 accommodation-title">Accommodation Details</h4>
                        {reservationData.booking_rooms.map((room, index) => (
                            <div key={index} className="room-details-block mb-4">
                                <div className="d-flex justify-content-between align-items-start">
                                    <div>
                                        <h5 className="fw-bold mb-1">{room.room_type.name}</h5>
                                        {/* Menggunakan kelas kustom .status-confirmed untuk menggantikan .text-success */}
                                        <span className="status-confirmed">{upperCase(room.status ?? '')}</span>
                                    </div>
                                    <span className="text-muted">Invoice: #{room.invoice_number}</span>
                                </div>
                                <hr className="my-3" />
                                <Row>
                                    <Col sm={6} className="mb-2"><strong>Check-in:</strong> {room.checkin_date}</Col>
                                    <Col sm={6} className="mb-2"><strong>Check-out:</strong> {room.checkout_date}</Col>
                                    <Col sm={6} className="mb-2"><strong>Guest:</strong> {room.customer_name} {room.customer_surname}</Col>
                                    <Col sm={6} className="mb-2"><strong>Guests:</strong> {room.total_guests}</Col>
                                    {/* PERBAIKAN: Tambahkan Nomor Kamar */}
                                    <Col sm={6} className="mb-2"><strong>Room Number:</strong> {room.room.room_number || 'N/A'}</Col>
                                    {/* PERBAIKAN: Tambahkan System Room */}
                                    <Col sm={6} className="mb-2"><strong>Rate Plan:</strong> {room.rate_plan.name}</Col>
                                </Row>

                                {/* PERBAIKAN: Tambahkan kebijakan pembatalan jika ada */}
                                {room.channex_meta?.policies && (
                                    <div className="mt-3">
                                        <h6 className="fw-bold">Cancellation Policy</h6>
                                        <p className="text-muted small">{room.channex_meta.policies}</p>
                                    </div>
                                )}

                                <div className="mt-3">
                                    <h6 className="fw-bold">Room Rate Breakdown</h6>
                                    {/* Menggunakan kelas kustom untuk tabel */}
                                    <Table striped size="sm" className="rate-table">
                                        <thead>
                                            <tr><th>Date</th><th className="text-end">Amount</th></tr>
                                        </thead>
                                        <tbody>
                                            {room.rates.map((rate, i) => (
                                                <tr key={i}>
                                                    <td>{rate.date}</td>
                                                    <td className="text-end">{`${numberFormat(rate.rate)} ${reservationData.channex_currency}`}</td>
                                                </tr>
                                            ))}
                                            {/* PERBAIKAN: Tambahkan komisi per kamar */}
                                            {room.channex_ota_commission > 0 && (
                                                <tr><td>Commission</td><td className="text-end text-danger">-{`${numberFormat(room.channex_ota_commission)} ${reservationData.channex_currency}`}</td></tr>
                                            )}
                                            <tr className="fw-bold">
                                                <td>Room Total</td>
                                                <td className="text-end">{`${numberFormat(room.total_rate)} ${reservationData.channex_currency}`}</td>
                                            </tr>
                                        </tbody>
                                    </Table>
                                </div>
                            </div>
                        ))}

                        {/* Footer */}
                        <div className="voucher-footer text-center mt-5 small">
                            <p className="mb-0">Issued by {reservationData.property?.name} (ID: {reservationData.property?.id})</p>
                            <p className="mb-0">Printed on: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
                        </div>
                    </div>
                </Container>
            )}
        </div>
    );
};

export default VoucherViewComponentNew2;